import { useSelector } from "react-redux";
import SFCellTextRenderer from "./renderers/cellTextRenderer";
import SFCheckboxCellRenderer from "./renderers/checkboxCellRenderer";
import SFURLRenderer from "./renderers/urlRenderer";
import SFEditRenderer from "./renderers/editRenderer";
import SFActionsRenderer from "./renderers/actionsRenderer";
import SFLoadingRenderer from "./renderers/loadingRenderer";
import SFBooleanRenderer from "./renderers/booleanRenderer";
import SFStatusRenderer from "./renderers/statusRenderer";
import SFProgressBarRenderer from "./renderers/progressBarRenderer";
import SFTrendRenderer from "./renderers/trendRenderer";
import SFColumnHeaderRenderer from "./renderers/columnHeaderRenderer";
import SFMasterDetailCellRenderer from "./renderers/masterDetailCellRenderer";
import SFRowDraggingRenderer from "./renderers/rowDraggingRenderer";

export const resolveGroupTreeFilterModel = (filterModel) => {
  let groupTreeFilters = [];
  //looking through the filter model to find if there are any groupTreeFilter types which
  //are the custom types declared in the virtual tree filter
  //if we find any we add them to the array above and delete their key from the actual filter model reference object
  Object.keys(filterModel).forEach((k) => {
    if (filterModel[k].filterType === "groupTreeFilter") {
      groupTreeFilters.push(filterModel[k]);
      delete filterModel[k];
    }
  });

  //we iterate through our group filters array
  //which is an array of objects with the following structure
  // {
  //   filterType: "groupTreeFilter",
  //   columns: {
  //     column1: {
  //       values: [{ id: "1", value: "name-1" } ...],
  //     },
  //     column2: {
  //       values: [{ id: "2", value: "name-2" } ...],
  //     },
  //   },
  // };
  // the value property is an object with column of what we want to add to the request filter model
  //its an object with the column names and their property values as array of objects with ids and values
  //the isGroupFilter property is used by the api to change the condition of filtering to OR instead of AND
  groupTreeFilters.forEach((treeFilter) => {
    Object.keys(treeFilter.columns).forEach((k) => {
      filterModel[k] = {
        filterType: "set",
        values: treeFilter.columns[k].values.map((y) => y.id),
        isGroupFilter: true,
      };
    });
  });
};

/**
 * Creates a search text filter object.
 * @param {string} value The search text.
 * @param {Array<string>} excludedProps The properties to exclude from the search.
 * @returns {string} An empty string if `val` is empty; otherwise, a stringified object containing search text and excluded properties for search.
 */
const getSearchTextFilterObject = (value = "", excludedProps = []) => {
  if (value === "") {
    return "";
  }

  const filterObject = {
    value: value,
    excludedProperties: excludedProps,
  };

  return JSON.stringify(filterObject);
};

/**
 * Updates the search text filter of a grid through its reference.
 * @param {React.RefObject} gridRef The grid reference.
 * @param {string} value The search text.
 * @param {Array<string>} excludedProps The properties to exclude from the search.
 * @returns {void}
 */
const updateSearchTextFilter = (gridRef, value, excludedProps) => {
  if (!gridRef?.current?.api) {
    return;
  }

  let filterModel = { ...gridRef.current.api.getFilterModel() };

  if ("searchText" in filterModel) {
    filterModel["searchText"].filter = getSearchTextFilterObject(
      value,
      excludedProps
    );
    // The filterType is not always set to "textSearch" when it should be (e.g. sometimes it is set to "text" instead of "textSearch").
    // The following line ensures that the filterType is always set to "textSearch" and works around the issue.
    // TODO: investigate further, probably a core component issue.
    filterModel["searchText"].filterType !== "textSearch" &&
      (filterModel["searchText"].filterType = "textSearch");
  } else {
    filterModel["searchText"] = {
      filterType: "textSearch",
      filter: getSearchTextFilterObject(value, excludedProps),
      type: "contains",
    };
  }

  gridRef.current.api.setFilterModel(filterModel);
};

/**
 * Returns the grid layout of a grid from the redux store.
 * @param {string} gridName The name of the grid to look for in the redux store.
 * @returns {Object} The grid layout of the grid.
 */
const useGridLayout = (gridName = "") => {
  if (!gridName) {
    return {};
  }

  // Get the grid layout from the redux store
  let gridLayout = useSelector(
    (state) =>
      state.gridLayout.find((gridLayout) => gridLayout.gridName === gridName) ??
      {}
  );

  return gridLayout;
};

/**
 * Returns the filter model of a grid from the redux store.
 * @param {string} gridName The name of the grid to look for in the redux store.
 * @returns {Object} The filter model of the grid.
 */
const useFilterModel = (gridName = "") => {
  if (!gridName) {
    return {};
  }

  // Get the filter model from the redux store
  let filterModel = useSelector(
    (state) =>
      state.gridLayout.find((gridLayout) => gridLayout.gridName === gridName)
        ?.filterModel ?? {}
  );

  return filterModel;
};

/**
 * Returns the search text filter value of a grid from the redux store.
 * @param {string} gridName The name of the grid to look for in the redux store.
 * @returns {string} The search text filter value.
 */
const useSearchTextFilterValue = (gridName = "") => {
  if (!gridName) {
    return "";
  }

  // Get the filter model from the redux store
  let filterModel = useFilterModel(gridName);

  // Initialize the searchTextFilterValue
  let searchTextFilterValue = "";

  // Check if searchText property exists and parse the filter object to get the value
  if ("searchText" in filterModel) {
    let searchTextFilterObject = JSON.parse(filterModel["searchText"].filter);
    searchTextFilterValue = searchTextFilterObject.value;
  }

  return searchTextFilterValue;
};

/**
 * Gets selected rows from a grid, handling both client-side and server-side row models.
 * For server-side grids with select all functionality, getSelectedRows() is not supported.
 * This function uses forEachNode to get loaded/rendered selected rows instead.
 * @param {Object} api The grid API instance.
 * @returns {Array} Array of selected row data from loaded rows.
 */
const getSelectedRowsFromApi = (api) => {
  if (!api) {
    return [];
  }

  const isServerSide = api.getGridOption("rowModelType") === "serverSide";

  if (isServerSide) {
    // For server-side grids, iterate through loaded nodes to get selected rows
    const selectedRows = [];
    api.forEachNode((node) => {
      if (node.isSelected() && node.data) {
        selectedRows.push(node.data);
      }
    });
    return selectedRows;
  }

  // For client-side grids, use the standard method
  return api.getSelectedRows() ?? [];
};

const sfDatagridHelpers = {
  resolveGroupTreeFilterModel,
  updateSearchTextFilter,
  useGridLayout,
  useFilterModel,
  useSearchTextFilterValue,
  getSelectedRowsFromApi,
};

export default sfDatagridHelpers;

/**
 * Returns all the custom components used in grid.
 * @param {object} customComponents The custom components defined out of the grid.
 * @returns {object} The custom components defined out of the grid plus default custom components of the grid.
 */
export const getCustomComponents = (customComponents = {}) => ({
  ...customComponents,
  SFActionsRenderer: SFActionsRenderer,
  SFCellTextRenderer: SFCellTextRenderer,
  SFCheckboxCellRenderer: SFCheckboxCellRenderer,
  // SFCustomDetailRenderer: otherProps.detailCellRenderer,
  agLoadingCellRenderer: SFLoadingRenderer,
  SFURLRenderer: SFURLRenderer,
  SFEditRenderer: SFEditRenderer,
  SFBooleanRenderer: SFBooleanRenderer,
  SFProgressBarRenderer: SFProgressBarRenderer,
  SFTrendRenderer: SFTrendRenderer,
  SFColumnHeaderRenderer: SFColumnHeaderRenderer,
  SFMasterDetailCellRenderer: SFMasterDetailCellRenderer,
  SFRowDraggingRenderer: SFRowDraggingRenderer,
  SFStatusRenderer: SFStatusRenderer,
});
